import React from 'react'
import copy from 'copy-to-clipboard'

import { routes } from '@sketch/constants'

import { Dropdown } from '@sketch/components'

import { useModalContext } from 'containers/ModalManager'
import { useToast } from '@sketch/toasts'

import {
  ProjectFragment,
  TrashedProjectFragment,
  useLeaveProjectMutation,
} from '@sketch/gql-types'

import DeleteProject from '../../modals/DeleteProject'
import DeleteProjectPermanently from '../../modals/DeleteProjectPermanently'
import RestoreProject from '../../modals/RestoreProject'
import { ProjectSharingModal } from '../../modals/ProjectSharingModal'

import { ConfirmRemoveMemberModal } from '../RemoveMemberModal'

interface ProjectProps {
  project: Pick<
    ProjectFragment | TrashedProjectFragment,
    'identifier' | 'name' | '__typename' | 'deletedAt'
  >
}

interface ProjectAndWorkspaceProps extends ProjectProps {
  workspaceId: string
}

interface RenameProjectActionProps {
  onRename: () => void
}

interface LeaveProjectActionProps {
  projectIdentifier: string
  projectName: string
}

export const LeaveProjectAction: React.FC<LeaveProjectActionProps> = ({
  projectIdentifier,
  projectName,
}) => {
  const { showModal, hideModal } = useModalContext()
  const [leaveProject] = useLeaveProjectMutation({
    variables: { projectIdentifier },
    onError() {
      // onError is used in `<RemoveMemberButton .../>` to handle the case
      // where a user is removing themselves we must show them the confirmation
      // dialog first. All other errors are ignored there.
      //
      // Since we are showing the confirmation dialog in all circumstances (and
      // providing `confirmed: true`) it is not necessary to handle this error
      // case.
      return
    },
  })

  const onConfirm = async () => {
    await leaveProject()
    hideModal()
  }

  return (
    <Dropdown.Item
      intent="negative"
      onClick={() => {
        showModal(ConfirmRemoveMemberModal, {
          projectName,
          onConfirm,
          onCancelAction: hideModal,
        })
      }}
    >
      Leave&hellip;
    </Dropdown.Item>
  )
}
export const DeleteProjectAction: React.FC<ProjectAndWorkspaceProps> = ({
  project,
  workspaceId,
}) => {
  const { showModal } = useModalContext()

  return (
    <Dropdown.Item
      intent="negative"
      onClick={() => {
        showModal(DeleteProject, {
          projectId: project.identifier,
          projectName: project.name,
          workspaceId,
        })
      }}
    >
      Delete&hellip;
    </Dropdown.Item>
  )
}

export const RenameProjectAction: React.FC<RenameProjectActionProps> = ({
  onRename,
}) => <Dropdown.Item onClick={onRename}>Rename&hellip;</Dropdown.Item>

export const CopyProjectAction: React.FC<ProjectAndWorkspaceProps> = ({
  project,
  workspaceId,
}) => {
  const { showToast } = useToast()

  return (
    <Dropdown.Item
      onClick={() => {
        const projectLink = (project.deletedAt
          ? routes.WORKSPACE_TRASH_PROJECT
          : routes.WORKSPACE_PROJECT
        ).create({
          workspaceId,
          projectId: project.identifier,
        })

        copy(`${window.location.origin}${projectLink}`)
        showToast('Link copied')
      }}
    >
      Copy Link
    </Dropdown.Item>
  )
}

export const RestoreProjectAction: React.FC<ProjectAndWorkspaceProps> = ({
  project,
  workspaceId,
}) => {
  const { showModal } = useModalContext()

  return (
    <Dropdown.Item
      onClick={() => showModal(RestoreProject, { project, workspaceId })}
    >
      Restore&hellip;
    </Dropdown.Item>
  )
}

export const DeleteProjectPermanentlyAction: React.FC<ProjectAndWorkspaceProps> = ({
  project,
  workspaceId,
}) => {
  const { showModal } = useModalContext()

  return (
    <Dropdown.Item
      intent="negative"
      onClick={() =>
        showModal(DeleteProjectPermanently, { project, workspaceId })
      }
    >
      Delete Permanently&hellip;
    </Dropdown.Item>
  )
}

export const ShareProjectAction: React.FC<{
  project: ProjectFragment
  workspaceId: string
}> = ({ project, workspaceId }) => {
  const { showModal } = useModalContext()

  return (
    <Dropdown.Item
      onClick={() =>
        showModal(ProjectSharingModal, {
          project,
          workspaceId,
        })
      }
    >
      Share&hellip;
    </Dropdown.Item>
  )
}
